// 세금계산서 출력물 1장. 데이터를 레이아웃으로 옮기기만 하는 순수 컴포넌트다.
// (@/lib · @/stores · @/contexts를 import하지 않는다)

import type {
  TaxInvoiceDocumentDto,
  TaxInvoiceItemRow,
  TaxInvoiceParty,
} from './tax-invoice-document-types';

/** 홈택스 양식과 동일하게 품목 행은 4개 고정이다 */
const ITEM_ROW_COUNT = 4;

function fmt(value: number | null | undefined) {
  return value === null || value === undefined ? '' : value.toLocaleString('ko-KR');
}

function PartyBlock({
  label,
  party,
  extraEmailRow,
}: {
  label: string;
  party: TaxInvoiceParty;
  extraEmailRow: boolean;
}) {
  return (
    <table className='tax-party'>
      <tbody>
        <tr>
          {/* 행 수: 등록번호·상호·주소·업태·이메일(+이메일2) */}
          <td className='tax-party-label' rowSpan={extraEmailRow ? 6 : 5}>
            {label}
          </td>
          <th>등록번호</th>
          <td className='tax-party-value'>{party.bizNo}</td>
          <th>종사업장번호</th>
          <td className='tax-party-value'>{party.subBizNo ?? ''}</td>
        </tr>
        <tr>
          <th>
            상호
            <br />
            (법인명)
          </th>
          <td className='tax-party-value'>{party.name}</td>
          <th>성명</th>
          <td className='tax-party-value'>{party.ceo}</td>
        </tr>
        <tr>
          <th>사업장주소</th>
          <td className='tax-party-value' colSpan={3}>
            {party.address}
          </td>
        </tr>
        <tr>
          <th>업태</th>
          <td className='tax-party-value'>{party.bizType ?? ''}</td>
          <th>종목</th>
          <td className='tax-party-value'>{party.bizItem ?? ''}</td>
        </tr>
        <tr>
          <th>이메일</th>
          <td className='tax-party-value' colSpan={3}>
            {party.email ?? ''}
          </td>
        </tr>
        {extraEmailRow && (
          <tr>
            <th>이메일</th>
            <td className='tax-party-value' colSpan={3}>
              {party.email2 ?? ''}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function ItemRow({ item }: { item: TaxInvoiceItemRow | null }) {
  if (!item) {
    return (
      <tr>
        {Array.from({ length: 9 }).map((_, i) => (
          <td key={i}>&nbsp;</td>
        ))}
      </tr>
    );
  }
  return (
    <tr>
      <td>{item.month}</td>
      <td>{item.day}</td>
      <td className='tax-item-name'>{item.itemName}</td>
      <td>{item.spec}</td>
      <td className='tax-num'>{fmt(item.qty)}</td>
      <td className='tax-num'>{fmt(item.unitPrice)}</td>
      <td className='tax-num'>{fmt(item.supplyAmt)}</td>
      <td className='tax-num'>{fmt(item.vatAmt)}</td>
      <td>{item.remark ?? ''}</td>
    </tr>
  );
}

function TaxInvoiceSheet({
  doc,
  items,
}: {
  doc: TaxInvoiceDocumentDto;
  items: TaxInvoiceItemRow[];
}) {
  const rows: (TaxInvoiceItemRow | null)[] = Array.from(
    { length: ITEM_ROW_COUNT },
    (_, i) => items[i] ?? null
  );

  return (
    <div className='tax-sheet'>
      <div className='tax-doc'>
        <table className='tax-head'>
          <tbody>
            <tr>
              <td className='tax-title'>{doc.titleText}</td>
              <th className='tax-approval-label'>승인번호</th>
              <td className='tax-approval-no'>{doc.approvalNo}</td>
            </tr>
          </tbody>
        </table>

        <div className='tax-parties'>
          <PartyBlock label='공급자' party={doc.supplier} extraEmailRow={false} />
          <PartyBlock
            label='공급받는자'
            party={doc.customer}
            extraEmailRow={true}
          />
        </div>

        <table className='tax-summary'>
          <tbody>
            <tr>
              <th>작성일자</th>
              <th>공급가액</th>
              <th>세액</th>
              <th>수정사유</th>
              <th>비고</th>
            </tr>
            <tr>
              <td>{doc.issueDate}</td>
              <td className='tax-num'>{fmt(doc.supplyAmt)}</td>
              <td className='tax-num'>{fmt(doc.vatAmt)}</td>
              <td>{doc.modifyReason}</td>
              <td className='tax-remark'>{doc.remark}</td>
            </tr>
          </tbody>
        </table>

        <table className='tax-items'>
          <colgroup>
            <col className='tax-col-m' />
            <col className='tax-col-d' />
            <col className='tax-col-item' />
            <col className='tax-col-spec' />
            <col className='tax-col-qty' />
            <col className='tax-col-price' />
            <col className='tax-col-supply' />
            <col className='tax-col-vat' />
            <col className='tax-col-remark' />
          </colgroup>
          <thead>
            <tr>
              <th>월</th>
              <th>일</th>
              <th>품목</th>
              <th>규격</th>
              <th>수량</th>
              <th>단가</th>
              <th>공급가액</th>
              <th>세액</th>
              <th>비고</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item, index) => (
              <ItemRow key={index} item={item} />
            ))}
          </tbody>
        </table>

        <table className='tax-footer'>
          <tbody>
            <tr>
              <th>합계금액</th>
              <th>현금</th>
              <th>수표</th>
              <th>어음</th>
              <th>외상미수금</th>
              <td className='tax-receipt' rowSpan={2}>
                이 금액을 ({doc.receiptType}) 함
              </td>
            </tr>
            <tr>
              <td className='tax-num'>{fmt(doc.totalAmt)}</td>
              <td className='tax-num'>{fmt(doc.cash)}</td>
              <td className='tax-num'>{fmt(doc.checkAmt)}</td>
              <td className='tax-num'>{fmt(doc.noteAmt)}</td>
              <td className='tax-num'>{fmt(doc.creditAmt)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * 품목이 4건을 넘으면 장을 나눈다. 양식의 품목 행이 4개 고정이라 행을 늘리면
 * 210mm 시트를 넘겨 다음 페이지로 흘러넘친다(머리글도 반복되지 않는다).
 * 머리글·공급자·작성일자·합계는 장마다 같은 값으로 반복한다.
 */
export function TaxInvoiceDocument({ doc }: { doc: TaxInvoiceDocumentDto }) {
  const sheets: TaxInvoiceItemRow[][] = [];
  for (let i = 0; i < Math.max(doc.items.length, 1); i += ITEM_ROW_COUNT) {
    sheets.push(doc.items.slice(i, i + ITEM_ROW_COUNT));
  }

  return (
    <>
      {sheets.map((items, index) => (
        <TaxInvoiceSheet key={index} doc={doc} items={items} />
      ))}
    </>
  );
}
